export function escapeHtml(s = "") {
  return String(s).replace(/[&<>"']/g, (m) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;",
  }[m]));
}

let _bcIdCounter = 0;
export function generateBlockId() {
  return "bc-" + Date.now().toString(36) + "-" + (++_bcIdCounter).toString(36);
}

const SECTION_LABEL_MAP = {
  "block": "Text",
  "text": "Text",
  "header": "Header",
  "footer": "Footer",
  "image": "Image",
  "book-card": "Book Card",
  "social": "Social",
  "divider": "Divider",
  "spacer": "Spacer",
  "video": "Video",
  "button": "Button",
  "banner": "Banner",
  "banner-image": "Banner Image",
  "two-col": "2 Columns",
  "image-text": "Image + Text",
  "section": "Section",
  "highlight": "Highlight",
  "quote": "Quote",
  "thumbnail-row": "Thumbnails",
  "image-gallery": "Image Gallery",
};

export function sectionWrap(innerHtml, type = "block", locked = false, radius = 14) {
  const blockId = generateBlockId();
  const friendlyLabel = SECTION_LABEL_MAP[type] || type;
  const dragHandle = locked
    ? `<div data-bc-lock contenteditable="false" style="position:absolute;top:4px;right:4px;font-size:12px;opacity:0.35;user-select:none;z-index:2;" title="Pinned block">&#x1F512;</div>`
    : `<div data-bc-drag contenteditable="false" style="position:absolute;top:4px;right:4px;cursor:grab;font-size:16px;user-select:none;z-index:2;padding:2px 4px;border-radius:4px;background:rgba(255,255,255,0.8);" title="Drag to reorder">&#x2807;</div>`;

  const sectionLabel = `<div data-bc-label contenteditable="false" style="position:absolute;top:2px;right:92px;font-size:9px;color:rgba(63,37,107,0.3);pointer-events:none;user-select:none;text-transform:uppercase;letter-spacing:0.5px;opacity:0;transition:opacity .15s ease;">${friendlyLabel}</div>`;


  return `
    <div data-bc-section="${type}" data-bc-id="${blockId}"${locked ? ' data-bc-locked="true"' : ""} style="margin:0;padding:0;position:relative;border-radius:${radius}px;">
      ${dragHandle}
      ${sectionLabel}
      ${innerHtml}
    </div>
  `;
}

export function makeFooterBlock() {
  // Auto-populate pen name from session if available
  let penName = "Your Author Name";
  try {
    const firstName = sessionStorage.getItem("custom:firstname") || "";
    const lastName = sessionStorage.getItem("custom:lastname") || "";
    const subdomain = sessionStorage.getItem("custom:subdomain") || "";
    if (firstName || lastName) {
      penName = `${firstName} ${lastName}`.trim();
    } else if (subdomain) {
      penName = subdomain;
    }
  } catch {}

  return sectionWrap(
    `
    <div style="
      padding:24px 20px;
      text-align:center;
      font-size:12px;
      opacity:0.7;
      border-top:1px solid rgba(0,0,0,0.1);
      width:calc(100% + 44px);
      margin-left:-22px;
      margin-right:-22px;
      position:relative;
      z-index:1;
    ">
      <p data-bc-focus="1" style="margin:0 0 6px 0;">
        You are receiving this email because you subscribed.
      </p>
      <p style="margin:0;">
        &copy; ${new Date().getFullYear()} ${escapeHtml(penName)}
      </p>
    </div>
    `,
    "footer"
  );
}

export function makeHeaderBlock(accentColor) {
  return sectionWrap(
    `
    <div
      data-header-bg="${escapeHtml(accentColor)}"
      data-bc-accent
      style="
        background:${accentColor};
        color:#ffffff;
        padding:28px 20px;
        text-align:center;
        width:calc(100% + 44px);
        margin-left:-22px;
        margin-right:-22px;
      "
    >
      <div
        contenteditable="true"
        data-bc-focus="1"
        data-placeholder="Newsletter title"
        style="font-size:24px;font-weight:700;"
      >Bookish Circle Newsletter</div>

      <div
        contenteditable="true"
        data-placeholder="Add a tagline..."
        style="margin-top:6px;font-size:14px;opacity:0.9;"
      ></div>
      <div
        data-header-nav
        style="
          margin-top:14px;
          display:flex;
          justify-content:center;
          gap:14px;
          flex-wrap:wrap;
          font-size:14px;
        "
      >
      </div>
    </div>
    `,
    "header",
    true
  );
}

export function makeTextBlock(text = "") {
  const safeText = text?.trim() ? escapeHtml(text) : "";

  return sectionWrap(
    `<p
      data-bc-focus="1"
      contenteditable="true"
      data-placeholder="Start typing\u2026"
      style="
        margin:0;
        padding:0 0 14px 0;
        line-height:1.6;
        word-break: break-word;
        overflow-wrap:anywhere;
      "
    >${safeText}</p>`,
    "text"
  );
}

export function makeThumbnailImageBlock(images = [], size = "large", align = "center") {
  const sizeMap = {
    small: 90,
    medium: 120,
    large: 160,
  };

  const imgSize = sizeMap[size] || 120;

  return sectionWrap(
    `
    <div
      data-thumb-row
      data-size="${size}"
      data-align="${align}"
      style="
        display:flex;
        gap:12px;
        justify-content:${align === "left" ? "flex-start" : align === "right" ? "flex-end" : "center"};
        margin:18px 0;
      "
    >
      ${images
        .map(
          (img) => `
          <img
            src="${img.src}"
            alt="${escapeHtml(img.alt || "")}"
            data-thumb
            data-link="${img.link || ""}"
            contenteditable="false"
            style="
              width:${imgSize}px;
              height:auto;
              display:block;
              cursor:pointer;
              outline:none;
            "
            onclick="window.__bcHandleImageClick && window.__bcHandleImageClick(this)"
          />
        `
        )
        .join("")}
    </div>
    `,
    "thumbnail-row"
  );
}

export function makeImageGalleryBlock() {
  return sectionWrap(
    `
    <div
      data-thumb-row
      data-size="large"
      data-align="center"
      style="
        display:flex;
        gap:12px;
        justify-content:center;
        flex-wrap:wrap;
        margin:18px 0;
      "
    >
      <div data-image-slot contenteditable="false"
        onclick="window.__bcImageSlotClick && window.__bcImageSlotClick(this)"
        style="
          width:160px;height:160px;
          border:2px dashed rgba(0,0,0,0.15);
          border-radius:12px;
          display:flex;align-items:center;justify-content:center;
          cursor:pointer;
          background:rgba(0,0,0,0.03);
        "
      >
        <div style="text-align:center;color:rgba(0,0,0,0.35);font-size:11px;pointer-events:none;">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" style="margin:0 auto 4px;">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
            <circle cx="8.5" cy="8.5" r="1.5"></circle>
            <path d="m21 15-5-5L5 21"></path>
          </svg>
          <div>Click to add image</div>
        </div>
      </div>
    </div>
    `,
    "image-gallery"
  );
}

export function makeBannerImageBlock({ src = "", href = "", alt = "" } = {}) {
  if (!src) {
    return sectionWrap(
      `
        <div style="margin:18px 0;">
          <div data-image-slot data-banner-slot contenteditable="false"
            onclick="window.__bcImageSlotClick && window.__bcImageSlotClick(this)"
            style="
              width:100%;height:180px;
              border:2px dashed rgba(0,0,0,0.15);
              border-radius:12px;
              display:flex;align-items:center;justify-content:center;
              cursor:pointer;
              background:rgba(0,0,0,0.03);
            "
          >
            <div style="text-align:center;color:rgba(0,0,0,0.35);font-size:12px;pointer-events:none;">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" style="margin:0 auto 6px;">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                <circle cx="8.5" cy="8.5" r="1.5"></circle>
                <path d="m21 15-5-5L5 21"></path>
              </svg>
              <div>Click to add banner image</div>
            </div>
          </div>
        </div>
      `,
      "banner-image"
    );
  }

  const img = `
    <img
      src="${src}"
      alt="${escapeHtml(alt)}"
      data-banner-img
      contenteditable="false"
      onclick="window.__bcHandleImageClick && window.__bcHandleImageClick(this)"
      style="
        width:100%;
        display:block;
        margin:0;
        padding:0;
        border:0;
        cursor:pointer;
      "
    />
  `;

  return sectionWrap(
    `
      <div style="margin:18px 0;">
        ${
          href
            ? `<a href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer">${img}</a>`
            : img
        }
      </div>
    `,
    "banner-image"
  );
}

export function makeButtonBlock(label, href, accentColor) {
  return sectionWrap(
    `
      <div style="margin:20px 0;text-align:center;">
        <a href="${escapeHtml(href || "#")}"
          data-bc-accent
          data-bc-button
          data-btn-label="${escapeHtml(label)}"
          data-btn-url="${escapeHtml(href || "")}"
          target="_blank"
          rel="noopener noreferrer"
          onclick="event.preventDefault(); window.__bcButtonClick && window.__bcButtonClick(this)"
          style="
            display:inline-block;
            background:${accentColor};
            color:#ffffff;
            padding:12px 24px;
            border-radius:10px;
            text-decoration:none;
            font-weight:700;
            cursor:pointer;
          ">
          ${escapeHtml(label)}
        </a>
      </div>
    `,
    "button"
  );
}

export function makeDividerBlock() {
  return `
    <div data-bc-section="divider" style="margin:24px 0;">
      <hr style="border:none;border-top:1px solid rgba(0,0,0,0.18);" />
    </div>
    ${makeTextBlock("")}
  `;
}

export function makeSpacerBlock() {
  return sectionWrap(`<div style="height:28px"></div>`, "spacer");
}

export function makeHighlightBlock(accentColor) {
  return sectionWrap(
    `
      <div data-bc-accent style="
        background:${accentColor}14;
        border-left:5px solid ${accentColor};
        padding:16px 16px;
        margin:16px 0;
        border-radius:10px;
      ">
        <strong>Highlight:</strong> <span data-bc-focus="1">Text here\u2026</span>
      </div>
    `,
    "highlight"
  );
}

export function makeSectionBlock(cardBg) {
  return sectionWrap(
    `
    <div
      style="
        background:${cardBg};
        border:1px solid rgba(0,0,0,0.08);
        padding:22px;
        border-radius:14px;
        margin:18px 0;
      "
    >
      <h2
        contenteditable="true"
        data-bc-focus="1"
        data-placeholder="Section title"
        style="
          margin:0 0 8px 0;
          font-size:22px;
          font-weight:700;
          line-height:1.25;
        "
      ></h2>

      <p
        contenteditable="true"
        data-placeholder="Section content\u2026"
        style="
          margin:0;
          line-height:1.6;
          word-break:break-word;
          overflow-wrap:anywhere;
        "
      ></p>
    </div>
    `,
    "section"
  );
}

export function makeQuoteBlock(accentColor) {
  return sectionWrap(
    `
      <div data-bc-accent style="
        margin:18px 0;
        padding:18px 18px;
        border-radius:14px;
        background: rgba(0,0,0,0.04);
        border-left: 5px solid ${accentColor};
        font-style: italic;
      ">
        <span data-bc-focus="1">\u201cA quote or featured line goes here.\u201d</span>
      </div>
    `,
    "quote"
  );
}

export function makeBannerBlock(accentColor) {
  return sectionWrap(
    `
      <div data-bc-accent style="
        background:${accentColor};
        color:#ffffff;
        border-radius:14px;
        padding:18px 18px;
        margin:18px 0;
        text-align:center;
        font-weight:800;
        letter-spacing:0.2px;
      ">
        <span data-bc-focus="1">Your Newsletter Header</span>
      </div>
    `,
    "banner"
  );
}

export function makeTwoColBlock() {
  return sectionWrap(
    `
      <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="margin:18px 0;">
        <tr>
          <td width="50%" valign="top" style="padding-right:10px;">
            <div style="border:1px solid rgba(0,0,0,0.08); border-radius:12px; padding:14px;">
              <h3 data-bc-focus="1" style="margin:0 0 8px 0;">Left</h3>
              <p style="margin:0;">Text\u2026</p>
            </div>
          </td>
          <td width="50%" valign="top" style="padding-left:10px;">
            <div style="border:1px solid rgba(0,0,0,0.08); border-radius:12px; padding:14px;">
              <h3 style="margin:0 0 8px 0;">Right</h3>
              <p style="margin:0;">Text\u2026</p>
            </div>
          </td>
        </tr>
      </table>
    `,
    "two-col"
  );
}

export function makeImageTextBlock() {
  return sectionWrap(
    `
      <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="margin:18px 0;">
        <tr>
          <td width="40%" valign="top" style="padding-right:12px;">
            <div style="border-radius:12px; overflow:hidden; border:1px solid rgba(0,0,0,0.10);">
              <img src="https://via.placeholder.com/520x360" alt="Image" style="width:100%; display:block;" />
            </div>
          </td>
          <td width="60%" valign="top" style="padding-left:12px;">
            <h3 data-bc-focus="1" style="margin:0 0 10px 0;">Title</h3>
            <p style="margin:0;">Text alongside the image\u2026</p>
          </td>
        </tr>
      </table>
    `,
    "image-text"
  );
}
